import { AppTopbar } from "../../../shell/AppTopbar";
import { Card, EmptyState, Page, Text } from "@marekl/adminer/components";
import { formatDateTime } from "@marekl/adminer/format";
import { Link } from "react-router-dom";
import { SeasonTrack } from "../../../components/SeasonTrack";
import { hasNoSeasonRegistration } from "../../../lib/memberState";
import { useDashboardAlerts, useDashboardStats } from "../../../lib/queries/dashboard";
import { useAuditLogs } from "../../../lib/queries/auditLogs";
import { useCompetitions, type Competition } from "../../../lib/queries/competitions";
import { useMyTeams } from "../../../lib/queries/teams";
import {
  useMe,
  hasAnyGlobalRole,
  hasTeamRole,
  canAdministerCompetitions,
  canViewCompetition,
} from "../../../auth/queries";
import { ROLE_LABEL } from "../../../lib/i18n/roles";
import { useSeason } from "../../../season";
import { dashboardBlocks } from "./blocks";
import { AttentionList } from "./AttentionList";
import { TeamReadinessCard } from "./TeamReadinessCard";
import { TeamCompetitionsCard } from "./TeamCompetitionsCard";
import { CompetitionsCard } from "./CompetitionsCard";
import { ActivityFeedCard } from "./ActivityFeedCard";
import { SeasonNumbersCard } from "./SeasonNumbersCard";
import styles from "./DashboardPage.module.css";

// The dashboard. docs/mockups/DESIGN.md §6 sets the running order and it is
// not arbitrary: the shape of the season first, what needs doing second, the
// reader's own work third, derived numbers last.
//
// The page is pinned to the ACTIVE season. The topbar switcher scopes rosters
// and registration, but „co je třeba udělat" is only ever about the season
// being written to — so rather than let the switcher silently do nothing here,
// the page says which season it is showing.

// The one role worth naming in the subtitle, most specific first.
const ROLE_PRIORITY = [
  "admin",
  "members_admin",
  "competitions_admin",
  "members_viewer",
  "competitions_viewer",
  "global_viewer",
  "federation_admin",
  "competition_organizer",
  "team_leader",
  "coach",
  "assistant",
  "athlete",
] as const;

export function DashboardPage() {
  const { data: me } = useMe();
  // The dashboard always reports the ACTIVE season — it is a "what needs
  // doing now" page, and there is nothing to do in a season that is over.
  // That is why it shows no season switcher.
  const { activeSeason } = useSeason();
  // The audit feed and the statistics: the membership office, and Čtení
  // všeho, who reads them too.
  const canSeeAdminData = hasAnyGlobalRole(me, "admin", "members_admin", "global_viewer");

  const alerts = useDashboardAlerts();
  const myTeams = useMyTeams();
  const competitions = useCompetitions(activeSeason ? { season_id: activeSeason.id } : {});
  // „Poslední aktivita" is about what changed, not about who was at their
  // keyboard — logins and impersonation would otherwise crowd out every real
  // edit. Excluded server-side; see the note on AuditLogFilters.
  const audit = useAuditLogs(
    { limit: 8, exclude_action_prefix: ["auth.", "impersonation."] },
    { enabled: canSeeAdminData },
  );
  const stats = useDashboardStats({ enabled: canSeeAdminData });

  const blocks = dashboardBlocks(me, myTeams.data, competitions.data);

  const role = ROLE_PRIORITY.find((r) => (me?.assignments ?? []).some((a) => a.role === r));
  const subtitleParts = [
    activeSeason ? `Sezóna ${activeSeason.code}` : "Bez aktivní sezóny",
    role ? ROLE_LABEL[role] : null,
    blocks.leadTeams.length === 1 ? blocks.leadTeams[0].name : null,
  ].filter(Boolean);

  // Where a stop on the axis leads depends on who is reading it. Whoever the
  // competition shell admits (canViewCompetition: the competition office, its
  // reader, its organizer) goes to the workspace; a leader to their own
  // entries; anyone else nowhere, because every other route would 403.
  const hrefFor = (c: Competition): string | undefined => {
    if (canViewCompetition(me, c.id)) {
      return `/soutez/${c.id}`;
    }
    const team = blocks.leadTeams[0];
    return team ? `/teams/${team.id}/souteze/${c.id}` : undefined;
  };

  // The season axis says where the season is; this table says what to do about
  // it. An admin needs both — and without it their left column is empty and
  // the activity feed sprawls across the full width.
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (competitions.data ?? [])
    .filter((c) => (c.date_to ?? c.date) >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 5);

  const left = [
    ...blocks.leadTeams.map((t) => (
      <TeamCompetitionsCard
        key={`comps-${t.id}`}
        teamId={t.id}
        teamName={t.name}
        competitions={competitions.data ?? []}
        isLoading={competitions.isLoading}
        error={competitions.error}
        notRegistered={hasNoSeasonRegistration(t.state_issues)}
        teamIsCmf={t.federation_is_primary}
      />
    )),
    blocks.organizedCompetitions.length > 0 ? (
      <CompetitionsCard
        key="organizer"
        title="Moje soutěže"
        competitions={blocks.organizedCompetitions}
        emptyTitle="Žádná soutěž"
        emptyDescription="V této sezóně nemáte přiřazenou žádnou soutěž."
        isLoading={competitions.isLoading}
        error={competitions.error}
      />
    ) : null,
    canSeeAdminData ? (
      <CompetitionsCard
        key="upcoming"
        title="Nadcházející soutěže"
        // Says which „Nadcházející soutěže" this is: a správce who also
        // leads a club gets the club's card of the same name right above.
        subtitle="celá federace"
        competitions={upcoming}
        emptyTitle="Žádná nadcházející soutěž"
        emptyDescription="V této sezóně už nejsou vypsané žádné další soutěže."
        isLoading={competitions.isLoading}
        error={competitions.error}
      />
    ) : null,
  ].filter(Boolean);

  const right = [
    ...blocks.teams.map((t) => (
      <TeamReadinessCard
        key={`team-${t.id}`}
        team={t}
        canRegister={
          hasTeamRole(me, t.id, "team_leader") &&
          t.status === "active" &&
          // The window binds everyone but the membership office, which
          // may register a club after it shuts.
          (activeSeason?.registration_open === true ||
            hasAnyGlobalRole(me, "admin", "members_admin"))
        }
      />
    )),
    canSeeAdminData ? (
      <ActivityFeedCard
        key="activity"
        entries={audit.data?.items ?? []}
        isLoading={audit.isLoading}
        error={audit.error}
      />
    ) : null,
  ].filter(Boolean);

  return (
    <>
      <AppTopbar crumbs={[{ label: "Přehled" }]} />
      <Page>
        <Page.Header title="Přehled" subtitle={subtitleParts.join(" · ")} />
        <Page.Content stack gap="lg">
          <SeasonTrack
            seasonCode={activeSeason?.code ?? "—"}
            phase={activeSeason?.phase}
            competitions={competitions.data ?? []}
            isLoading={competitions.isLoading}
            error={competitions.error}
            hrefFor={hrefFor}
            emptyDescription={
              canAdministerCompetitions(me)
                ? "Založte první soutěž a objeví se tu jako zastávka na ose sezóny."
                : "Federace zatím pro tuto sezónu nevypsala žádnou soutěž."
            }
            emptyAction={
              canAdministerCompetitions(me) ? (
                <Link to="/admin/souteze" className={styles.action}>
                  Založit soutěž
                </Link>
              ) : undefined
            }
          />

          <AttentionList
            alerts={alerts.data}
            isLoading={alerts.isLoading}
            error={alerts.error}
          />

          {left.length > 0 && right.length > 0 ? (
            <div className={styles.split}>
              <div className={styles.column}>{left}</div>
              <div className={styles.column}>{right}</div>
            </div>
          ) : (
            <>
              {left}
              {right}
            </>
          )}

          {/* Gated on the fetch, not just on the result: dashboardBlocks reads
              an undefined myTeams.data as "no teams", so this card told every
              member of every team that they belonged to none of them, on every
              single load, until their roster arrived. */}
          {blocks.isEmpty && !myTeams.isLoading && (
            <Card>
              <Card.Content>
                <EmptyState
                  title="Váš účet zatím není spojen s žádným týmem"
                  description="Až vás vedoucí přidá na soupisku, uvidíte tu stav svého týmu a jeho přihlášky. Kalendář sezóny výše je veřejný pro všechny přihlášené."
                />
              </Card.Content>
            </Card>
          )}

          {canSeeAdminData && (
            <SeasonNumbersCard
              stats={stats.data}
              isLoading={stats.isLoading}
              error={stats.error}
            />
          )}

          {stats.data && (
            <Text as="p" className={styles.systemLine}>
              {`verze ${stats.data.system.version || "dev"} · běží od ${formatDateTime(stats.data.system.started_at)} · čas serveru ${formatDateTime(stats.data.system.server_now)}`}
            </Text>
          )}
        </Page.Content>
      </Page>
    </>
  );
}
